import type { NotificationChannelType } from "./types"

/** How many destinations are spelled out before the rest collapse into a counter. */
const INLINE_LIMIT = 2

export interface RecipientSummary {
  /** Short form for the row — truncated with a "+N" tail. */
  short: string
  /** Every destination, for the `title` tooltip. */
  full: string
  count: number
}

/**
 * Who actually receives from this channel. Names alone hide the mistake worth catching:
 * a channel called "Ops" pointing at someone's personal inbox.
 */
export function describeRecipients(
  type: NotificationChannelType,
  config: Record<string, unknown> | null | undefined,
): RecipientSummary {
  const destinations = type === "email" ? emailRecipients(config) : telegramChats(config)

  if (destinations.length === 0) {
    return { short: type === "email" ? "No recipients" : "No chats", full: "", count: 0 }
  }

  const shown = destinations.slice(0, INLINE_LIMIT).join(", ")
  const rest = destinations.length - INLINE_LIMIT

  return {
    short: rest > 0 ? `${shown}… (+${rest})` : shown,
    full: destinations.join(", "),
    count: destinations.length,
  }
}

/**
 * One-line "who receives" summary for a channel row. Telegram leads with the bot because a
 * list of chats says nothing about which bot posts into them.
 */
export function describeChannelDestination(
  type: NotificationChannelType,
  config: Record<string, unknown> | null | undefined,
): { short: string; full: string } {
  const recipients = describeRecipients(type, config)
  const prefix = type === "telegram" ? `${telegramBotLabel(config)} · ` : ""

  return { short: `${prefix}${recipients.short}`, full: recipients.full }
}

/** `@bot_username` when known — a Telegram channel's chats say nothing about which bot posts them. */
export function telegramBotLabel(config: Record<string, unknown> | null | undefined): string {
  const username = config?.bot_username
  return typeof username === "string" && username ? `@${username}` : "bot"
}

function emailRecipients(config: Record<string, unknown> | null | undefined): string[] {
  const recipients = config?.recipients
  return Array.isArray(recipients) ? recipients.filter((r): r is string => typeof r === "string" && !!r) : []
}

function telegramChats(config: Record<string, unknown> | null | undefined): string[] {
  const chats = config?.chats
  if (!Array.isArray(chats)) return []

  return chats
    .map((chat) => {
      const entry = chat as { label?: unknown; id?: unknown }
      if (typeof entry.label === "string" && entry.label) return entry.label
      return typeof entry.id === "string" && entry.id ? entry.id : null
    })
    .filter((label): label is string => label !== null)
}

/**
 * Why a channel would deliver nothing today, in the dispatcher's own terms
 * (`isChannelDeliverable`: enabled AND usable). `null` = it will deliver.
 *
 * `deliverable` must come from the server: the client's config has no
 * `bot_token_encrypted`, so recomputing it here would call every Telegram channel broken.
 */
export function deliveryWarning(channel: {
  type: NotificationChannelType
  enabled: boolean
  deliverable: boolean
}): string | null {
  if (!channel.enabled) return "off"
  if (channel.deliverable) return null
  return channel.type === "telegram" ? "no chats" : "no recipients"
}
